import { type ReactNode } from "react";
import { fmtDate, type UserActivity } from "../activity";

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: string;
  children: ReactNode;
}) {
  return (
    <div>
      <h4 className="flex items-center gap-2 text-sm font-semibold text-foreground-800">
        <i className={`${icon} text-base text-foreground-500`} aria-hidden="true"></i>
        {title}
      </h4>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function EmptyHint({ text }: { text: string }) {
  return <p className="text-sm text-foreground-500">{text}</p>;
}

export default function ActivityLearning({ activity }: { activity: UserActivity }) {
  const totalMinutes = activity.studySessions.reduce((s, x) => s + x.minutes, 0);

  return (
    <div className="space-y-6">
      {/* Çalışma süresi */}
      <Section title="Çalışma Süresi" icon="ri-time-line">
        {activity.studySessions.length === 0 ? (
          <EmptyHint text="Henüz çalışma kaydı yok." />
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-foreground-600">
              Toplam <span className="font-semibold text-foreground-900">{totalMinutes} dk</span> ·{" "}
              {activity.studySessions.length} oturum
            </p>
            <div className="flex flex-wrap gap-2">
              {activity.studySessions.slice(0, 10).map((s) => (
                <span
                  key={s.id}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-background-100 text-xs text-foreground-700 whitespace-nowrap"
                >
                  {s.subject ?? "Genel"} · {s.minutes} dk · {fmtDate(s.studied_on)}
                </span>
              ))}
            </div>
          </div>
        )}
      </Section>

      {/* Ders ilerlemesi */}
      <Section title="Ders İlerlemesi" icon="ri-book-open-line">
        {activity.courseProgress.length === 0 ? (
          <EmptyHint text="Henüz ders ilerlemesi yok." />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {activity.courseProgress.map((c) => (
              <div
                key={c.course_id}
                className="rounded-md border border-background-200 bg-background-50 p-3"
              >
                <p className="text-sm font-medium text-foreground-900 truncate">{c.course_id}</p>
                <p className="mt-1 text-xs text-foreground-600">
                  {c.completed_topics} konu tamamlandı · {c.weak_count} zayıf konu
                </p>
                <p className="mt-0.5 text-xs text-foreground-500">
                  Son çalışma: {fmtDate(c.last_studied)}
                </p>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Konu performansı */}
      <Section title="Ders Performansı" icon="ri-bar-chart-2-line">
        {activity.subjectPerformance.length === 0 ? (
          <EmptyHint text="Henüz ders bazlı performans verisi yok." />
        ) : (
          <div className="space-y-2.5">
            {activity.subjectPerformance.map((p) => {
              const ratio = p.max_net > 0 ? (p.net / p.max_net) * 100 : 0;
              return (
                <div key={p.id}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium text-foreground-800">
                      {p.subject ?? "—"}
                    </span>
                    <span className="text-foreground-600">
                      {p.net.toFixed(2)} / {p.max_net.toFixed(0)} net
                    </span>
                  </div>
                  <div className="mt-1 h-2 rounded-full bg-background-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-accent-500 transition-all"
                      style={{ width: `${Math.min(100, Math.max(0, ratio))}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* Zayıf konular */}
      <Section title="Zayıf Konular" icon="ri-alert-line">
        {activity.weakTopics.length === 0 ? (
          <EmptyHint text="Zayıf konu tespit edilmemiş." />
        ) : (
          <div className="space-y-2">
            {activity.weakTopics.map((w) => (
              <div
                key={w.id}
                className="flex items-center justify-between gap-3 rounded-md border border-background-200 bg-background-50 px-3 py-2.5"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground-900 truncate">
                    {w.topic ?? "—"}
                  </p>
                  <p className="text-xs text-foreground-500">{w.subject ?? "—"}</p>
                </div>
                <span className="shrink-0 text-sm font-semibold text-accent-800">
                  %{w.mastery_percent}
                </span>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}